import _ from "lodash";
import AttributeValues from "./AttributeValues";
import {
  getRecordsToOverride,
  getValueTypeByAttribute,
  ExtensionURLs,
  AttributeLabels,
  ActionLabels,
  getOverrideAuditExtensionURLs,
  getOverrideRestoreAuditExtensionURLs,
  getOverrideExtensionURLByAttribute,
  getRestoreExtensionURLByAttribute,
  getAttributeFields,
  normalizeAttributeName,
  getPhoneExtensionURLs,
  getPhoneExtensionSections,
  GenericRestoreURLs,
  GenericOverrideURLs,
} from "./Utils";
import {
  createOverrideAuditExtension,
  createRestoreAuditExtensions,
} from "../MultipleAttributeOverride/PrepareOverrides";
import {
  getOverrideType,
  createRestoreExtensions,
} from "../RecordOverrideUtil";

const PrepareOverrides = (
  attribute,
  action,
  system,
  attrCode,
  code,
  newValue,
  oldValue,
  attributeValues,
  auditDetails,
) => {
  /**
   * Create the initial patient object from the record. The requestTemplate contains all previous overrides/restores.
   *
   * @param {Map} recordMap - The record object containing patient data.
   * @returns {Object} - The initial request object.
   */
  const initialRequestObject = (recordMap) => {
    return Object.assign(
      {},
      {
        resourceType: recordMap.get("resourceType"),
        id: recordMap.get("id"),
        meta: recordMap.get("meta"),
      },
      recordMap.get("requestTemplate"),
    );
  };

  /**
   * Inactivate the given object by removing the extension and setting use to "old". This value is to be used as the
   * values to be overridden to in a multi value attribute.
   *
   * @param {Object} obj - The object to be inactivated.
   * @returns {Object} - The inactivated object.
   */
  const inactivateObject = (obj) => {
    const inactiveObj = { ...obj };

    delete inactiveObj["extension"];
    inactiveObj["use"] = "old";

    return inactiveObj;
  };

  /**
   * Create phone extensions from the new value map.
   *
   * @param {Map} newValueMap - The map of new values for the attribute.
   * @returns {Array} - An array of phone extension objects.
   */
  const createPhoneExtensions = (newValueMap) => {
    const phoneValue = newValueMap?.get("value");

    const extensionsArray = getPhoneExtensionSections().map((section) => ({
      url: section.url,
      valueString: phoneValue.substring(section.start, section.end),
    }));

    extensionsArray.push({
      url: ExtensionURLs.phoneCountry,
      valueString: "1",
    });

    return extensionsArray;
  };

  /**
   * Prepare the request object for restore of top-level extension attributes.
   *
   * @param {string} attribute - The attribute being processed.
   * @param {Object} record - The record object.
   * @param {Object} requestObject - The current request object.
   * @returns {Object} - The modified request object after removing the override extensions.
   */
  const getSingleValueRestoreRequestObject = (
    attribute,
    recordMap,
    requestObject,
  ) => {
    const updatedRequestObject = _.cloneDeep(requestObject);
    const currentValue = recordMap.get(attribute);
    const overrideAuditURLs = Object.values(
      getOverrideAuditExtensionURLs(attribute),
    );
    const restoreAuditURLs = Object.values(
      getOverrideRestoreAuditExtensionURLs(attribute),
    );

    const overrideExtensionURL = getOverrideExtensionURLByAttribute(attribute);
    const extensionsToRemove = [
      overrideExtensionURL,
      ...overrideAuditURLs,
      ...restoreAuditURLs,
    ];
    const restoreAuditExtensions = createRestoreAuditExtensions(
      auditDetails,
      attribute,
    );

    const filteredTopLevelExtensions = (
      updatedRequestObject["extension"] || []
    ).filter((ext) => !extensionsToRemove.includes(ext.url));

    // Combine existing and new restore extension objects
    const finalTopLevelExtensionObjects = [
      ...filteredTopLevelExtensions,
      ...restoreAuditExtensions,
    ];

    // Replace the "extension" property in requestObject
    updatedRequestObject["extension"] = finalTopLevelExtensionObjects;
    delete updatedRequestObject[attribute];

    return updatedRequestObject;
  };

  /**
   * Prepare the request object for replace of top-level extension attributes.
   *
   * @param {string} attribute - The attribute being processed.
   * @param {Object} record - The record object.
   * @param {string} action - The action being performed.
   * @param {any} oldValue - The old value of the attribute.
   * @param {any} newValue - The new value of the attribute.
   * @param {Object} requestObject - The current request object.
   * @returns {Object} - The modified request object with the override extension.
   */
  const getSingleValueReplaceRequestObject = (
    attribute,
    recordMap,
    action,
    oldValue,
    newValue,
    requestObject,
  ) => {
    const updatedRequestObject = _.cloneDeep(requestObject);
    const attributeValueType = getValueTypeByAttribute(attribute);
    const restoreExtensionUrls = Object.values(
      getOverrideRestoreAuditExtensionURLs(attribute),
    );

    const overrideExtension = new Object();
    overrideExtension["url"] = getOverrideExtensionURLByAttribute(attribute);
    overrideExtension[attributeValueType] =
      attribute === AttributeLabels.GENDER ? oldValue.toLowerCase() : oldValue;
    const overrideAuditExtension = createOverrideAuditExtension(
      auditDetails,
      attribute,
    );

    // Ensure the top-level extension array exists
    updatedRequestObject["extension"] = updatedRequestObject["extension"] || [];
    // Remove any previous restore audit extensions for this attribute
    updatedRequestObject["extension"] = updatedRequestObject[
      "extension"
    ].filter((ext) => !restoreExtensionUrls.includes(ext.url));
    // Add new override extension
    updatedRequestObject["extension"].push(overrideExtension);
    // Add audit details inside override extension
    overrideAuditExtension.forEach((ext) => {
      updatedRequestObject["extension"].push(ext);
    });
    // Set the new attribute value
    updatedRequestObject[attribute] =
      attribute === AttributeLabels.GENDER ? newValue.toLowerCase() : newValue;

    return updatedRequestObject;
  };

  /**
   * Prepare the request object for multi-value attribute restore.
   *
   * @param {string} attribute - The attribute being processed.
   * @param {Object} record - The record object.
   * @param {Object} requestObject - The current request object.
   * @returns {Object} - The modified request object after removing the override extensions.
   */
  const getMultiValueRestoreRequestObject = (
    attribute,
    recordMap,
    requestObject,
  ) => {
    const updatedRequestObject = _.cloneDeep(requestObject); // Includes previous overrides and restores
    const attributeItem = _.cloneDeep(recordMap.get("itemJSON")); // The item to be restored
    const attributeName = normalizeAttributeName(attribute); // Get the correct attribute name, e.g. "phone" and "email" should be "telecom"

    // Ensure an attribute array exists in the request object.
    updatedRequestObject[attributeName] =
      updatedRequestObject[attributeName] || [];

    // Remove the attribute item from the original request object.
    updatedRequestObject[attributeName] = updatedRequestObject[
      attributeName
    ].filter((item) => !_.isEqual(item, attributeItem));

    const restoreItem = _.cloneDeep(recordMap.get("itemJSON"));

    // Create restore extensions (restore-updatedBy, restore-reason, restore-timestamp and restore extensions)
    const restoreExtensions = createRestoreExtensions(attribute, auditDetails);

    const overrideType = getOverrideType(attribute, attributeItem);

    // If the original item is a "new" override, return an empty object as a "new" override does not have an original
    // item, otherwise get the overridden item.
    const originalItem =
      overrideType?.toLowerCase() === ActionLabels.NEW
        ? {}
        : attributeItem?.extension?.find(
            (ext) => ext.url === getOverrideExtensionURLByAttribute(attribute),
          )?.[getValueTypeByAttribute(attribute)] || {};

    // Add restore extensions to the item extension
    originalItem.extension = [
      ...(originalItem.extension || []),
      ...restoreExtensions,
    ];

    // Add the item with restore extensions to the request object.
    updatedRequestObject[attributeName] =
      updatedRequestObject[attributeName] || [];
    updatedRequestObject[attributeName].push(originalItem);

    return updatedRequestObject;
  };

  /**
   * Prepare the request object for multi-value attribute replace.
   *
   * @param {string} attribute - The attribute being processed.
   * @param {Object} record - The record object.
   * @param {Object} newValuesMap - The map of new values for the attribute.
   * @param {Object} requestObject - The current request object.
   * @returns {Object} - The modified request object with the override extension.
   */
  const getMultiValueReplaceRequestObject = (
    attribute,
    recordMap,
    newValuesMap,
    requestObject,
  ) => {
    const updatedRequestObject = _.cloneDeep(requestObject); // Includes previous overrides and restores
    const originalAttributeItem = _.cloneDeep(recordMap.get("itemJSON")); // The original attribute item
    const attributeFields = getAttributeFields(attribute); // The fields to be updated for the attribute
    const overrideObject = createMultiValueOverrideObject(attribute, recordMap); // The new override object
    const attributeName = normalizeAttributeName(attribute); // Get the correct attribute name, e.g. "phone" and "email" should be "telecom"

    // Special handling for phone attribute to create phone extensions
    if (attribute == AttributeLabels.PHONE) {
      const newPhoneValueExtensions = createPhoneExtensions(newValuesMap);
      overrideObject["extension"].push(...newPhoneValueExtensions);
    }

    // Populate overrideObject with updated fields
    attributeFields.forEach((field) => {
      // For use, period, type, and system get values from the original record (these values shouldn't change)
      if (
        ["use", "period", "type", "system"].includes(field) &&
        recordMap.has(field)
      ) {
        overrideObject[field] = recordMap.get(field);
      } else if (
        // For all other fields, get values from the newValuesMap
        !["use", "period", "type", "system"].includes(field) &&
        newValuesMap.has(field)
      ) {
        overrideObject[field] = newValuesMap.get(field);
      }
    });

    // Ensure an attribute array exists in the request object.
    updatedRequestObject[attributeName] =
      updatedRequestObject[attributeName] || [];

    // Remove the original attribute item from the request object.
    updatedRequestObject[attributeName] = updatedRequestObject[
      attributeName
    ].filter((item) => !_.isEqual(item, originalAttributeItem));

    // Add the new override object to the request object.
    updatedRequestObject[attributeName].push(overrideObject);

    return updatedRequestObject;
  };

  /**
   * Prepare the request object for multi-value attribute inactivate.
   *
   * @param {string} attribute - The attribute being processed.
   * @param {Object} record - The record object.
   * @param {Object} requestObject - The current request object.
   * @returns {Object} - The modified request object with the inactivated extension.
   */
  const getMultiValueInactivateRequestObject = (
    attribute,
    recordMap,
    requestObject,
  ) => {
    const originalAttributeItem = _.cloneDeep(recordMap.get("itemJSON")); // The original attribute item
    const updatedRequestObject = _.cloneDeep(requestObject); // Includes previous overrides and restores
    const overrideObject = createMultiValueOverrideObject(attribute, recordMap); // The new override object
    const valueType = getValueTypeByAttribute(attribute); // The value type for the attribute
    const attributeName = normalizeAttributeName(attribute); // Get the correct attribute name, e.g. "phone" and "email" should be "telecom"

    // Special handling for phone attribute to create phone extensions
    if (attribute == AttributeLabels.PHONE) {
      const newPhoneValueExtensions = createPhoneExtensions(recordMap);
      overrideObject["extension"].push(...newPhoneValueExtensions);
    }

    // Combine the override object with the inactivated original attribute item
    const requestObjectItem = Object.assign(
      overrideObject,
      inactivateObject(originalAttributeItem),
    );

    // Ensure an attribute array exists in the request object.
    updatedRequestObject[attributeName] =
      updatedRequestObject[attributeName] || [];

    // Remove the original attribute item from the request object.
    updatedRequestObject[attributeName] = updatedRequestObject[
      attributeName
    ].filter((item) => !_.isEqual(item, originalAttributeItem));

    // Add the new override object to the request object.
    updatedRequestObject[attributeName].push(requestObjectItem);

    return updatedRequestObject;
  };

  /**
   * Prepare the request object for multi-value extension attribute invalidate.
   *
   * @param {string} attribute - The attribute being processed.
   * @param {Object} record - The record object.
   * @param {Object} requestObject - The current request object.
   * @returns {Object} - The modified request object with the invalidated extension.
   */
  const getMultiValueInvalidateRequestObject = (
    attribute,
    recordMap,
    requestObject,
  ) => {
    const originalAttributeItem = _.cloneDeep(recordMap.get("itemJSON"));
    const updatedRequestObject = _.cloneDeep(requestObject);
    const overrideObject = createMultiValueOverrideObject(attribute, recordMap);
    const attributeName = normalizeAttributeName(attribute);

    // Ensure an attribute array exists in the request object.
    updatedRequestObject[attributeName] =
      updatedRequestObject[attributeName] || [];

    // Remove the original attribute item from the request object.
    updatedRequestObject[attributeName] = updatedRequestObject[
      attributeName
    ].filter((item) => !_.isEqual(item, originalAttributeItem));

    // Add the new override object to the request object.
    updatedRequestObject[attributeName].push(overrideObject);

    return updatedRequestObject;
  };

  /**
   * Create the previous values object for multi-value extension overrides.
   *
   * @param {string} attribute - The attribute being processed.
   * @param {Object} record - The record object.
   * @returns {Object} - The previous values object.
   */
  const createPreviousValuesObject = (attribute, recordMap) => {
    // Get the restore extensions
    const restoreAuditExtensions = Object.values(
      getOverrideRestoreAuditExtensionURLs(attribute),
    );
    const restoreExtension = ExtensionURLs.genericRestore;

    // Get the original attribute item and filter out restore-related extensions
    const attributeItem = _.cloneDeep(recordMap.get("itemJSON"));
    attributeItem["extension"] = attributeItem["extension"]?.filter(
      (ext) =>
        !restoreAuditExtensions.includes(ext.url) &&
        ext.url !== restoreExtension,
    );

    // If after filtering restore extensions the extension key is empty, remove extension
    if (_.isEmpty(attributeItem?.extension)) {
      delete attributeItem.extension;
    }

    const attributeValueType = getValueTypeByAttribute(attribute);
    const previousValues = new Object();
    previousValues["url"] = ExtensionURLs.genericOverride;
    previousValues[attributeValueType] = attributeItem;

    return previousValues;
  };

  /**
   * Prepare attribute item extensions by filtering out phone extensions if necessary.
   *
   * @param {string} attribute - The attribute being processed.
   * @param {Object} record - The record object.
   * @returns {Array} - The prepared array of attribute item extensions.
   */
  const prepareAttributeItemExtensions = (attribute, recordMap) => {
    const restoreAuditExtensions = Object.values(
      getOverrideRestoreAuditExtensionURLs(attribute),
    );
    const restoreExtension = ExtensionURLs.genericRestore;

    const attributeItem = _.cloneDeep(recordMap.get("itemJSON"));
    const attributeItemExtension = attributeItem.extension || [];
    const restoreExtensionsFiltered = attributeItemExtension.filter(
      (ext) =>
        !restoreAuditExtensions.includes(ext.url) &&
        ext.url !== restoreExtension,
    );

    if (attribute === AttributeLabels.PHONE) {
      return restoreExtensionsFiltered.filter(
        (ext) => !getPhoneExtensionURLs().includes(ext.url),
      );
    }

    return restoreExtensionsFiltered;
  };

  /**
   * Create the multi-value override object. Creates a new object that includes extensions
   *
   * @param {string} attribute - The attribute being processed.
   * @param {Object} record - The record object.
   * @returns {Object} - The multi-value override object.
   */
  const createMultiValueOverrideObject = (attribute, recordMap) => {
    // Get the value type to be used in the override object
    const attributeValueType = getValueTypeByAttribute(attribute);

    // Create the new override object
    const overrideObject = new Object();
    overrideObject["extension"] = new Array();
    const previousValues = createPreviousValuesObject(attribute, recordMap);
    const overrideAuditExtensions = createOverrideAuditExtension(auditDetails);
    overrideObject["extension"].push(previousValues);
    overrideObject["extension"].push(...overrideAuditExtensions);

    const attributeItemExtensions = prepareAttributeItemExtensions(
      attribute,
      recordMap,
    );

    if (action !== ActionLabels.INVALID) {
      overrideObject["extension"].push(...attributeItemExtensions);
    }

    return overrideObject;
  };

  /**
   * Prepare the top-level extension attributes based on the action.
   *
   * @param {string} attribute - The attribute being processed.
   * @param {Object} record - The record object.
   * @param {string} action - The action being performed.
   * @param {any} oldValue - The old value of the attribute.
   * @param {any} newValue - The new value of the attribute.
   * @param {Object} requestObject - The current request object.
   * @returns {Object} - The modified request object.
   */
  const prepareSingleValueAttributeRequest = (
    attribute,
    recordMap,
    action,
    oldValue,
    newValue,
    requestObject,
  ) => {
    switch (action) {
      case ActionLabels.RESTORE:
        return getSingleValueRestoreRequestObject(
          attribute,
          recordMap,
          requestObject,
        );
      default:
        return getSingleValueReplaceRequestObject(
          attribute,
          recordMap,
          action,
          oldValue,
          newValue,
          requestObject,
        );
    }
  };

  /**
   * Prepare the multi-value extension attributes based on the action.
   *
   * @param {string} attribute - The attribute being processed.
   * @param {Object} record - The record object.
   * @param {string} action - The action being performed.
   * @param {Object} newValuesMap - The map of new values for the attribute.
   * @param {Object} requestObject - The current request object.
   * @returns {Object} - The modified request object.
   */
  const prepareMultiValueAttributeRequest = (
    attribute,
    recordMap,
    action,
    newValuesMap,
    requestObject,
  ) => {
    switch (action) {
      case ActionLabels.RESTORE:
        return getMultiValueRestoreRequestObject(
          attribute,
          recordMap,
          requestObject,
        );
      case ActionLabels.REPLACE:
        return getMultiValueReplaceRequestObject(
          attribute,
          recordMap,
          newValuesMap,
          requestObject,
        );
      case ActionLabels.INACTIVE:
        return getMultiValueInactivateRequestObject(
          attribute,
          recordMap,
          requestObject,
        );
      case ActionLabels.INVALID:
        return getMultiValueInvalidateRequestObject(
          attribute,
          recordMap,
          requestObject,
        );
    }
  };

  /**
   * Get the new value map from the attribute values based on the attribute type and selected criteria.
   *
   * @param {Array} attributeValues - The array of attribute values for the record.
   * @param {string} attribute - The attribute being processed.
   * @param {string} newValue - The selected new value for the attribute.
   * @param {string} system - The selected system (if applicable).
   * @param {string} code - The selected code (if applicable).
   * @returns {Map} - The map of new values for the attribute.
   */
  const getNewValuesMap = (
    attributeValues,
    attribute,
    newValue,
    system,
    code,
  ) => {
    const normalizedSystem = system === "[No System]" ? undefined : system;
    const normalizedCode = code === "" ? undefined : code;

    switch (attribute) {
      case AttributeLabels.IDENTIFIER:
        return attributeValues
          .flat()
          .find(
            (v) =>
              v?.get("displayValue") === newValue &&
              v?.get("system") === normalizedSystem &&
              v?.get("code") === normalizedCode,
          );
      default:
        return attributeValues
          .flat()
          .find((v) => v?.get("displayValue") === newValue);
    }
  };

  /**
   * Finds the new value map from the attribute values by matching the selected newValue with the Maps displayValue. This
   * way we access all of the individual fields of the new value for multi-field attributes.
   */
  const newValuesMap = getNewValuesMap(
    attributeValues,
    attribute,
    newValue,
    system,
    code,
  );

  const recordsToOverride = getRecordsToOverride(
    attributeValues,
    attribute,
    system,
    attrCode,
    action,
    oldValue,
    code,
  );

  const finalRequestObjects = recordsToOverride.map((recordMap) => {
    const requestObject = initialRequestObject(recordMap);
    requestObject.meta.lastUpdated = "";

    switch (attribute) {
      case AttributeLabels.BIRTHDATE:
      case AttributeLabels.GENDER:
        return prepareSingleValueAttributeRequest(
          attribute,
          recordMap,
          action,
          oldValue,
          newValue,
          requestObject,
        );
      default:
        return prepareMultiValueAttributeRequest(
          attribute,
          recordMap,
          action,
          newValuesMap,
          requestObject,
        );
    }
  });

  return finalRequestObjects;
};

export default PrepareOverrides;
